import Modal from 'LuxKit/Modal/Modal'
import VillasPageAmenitiesModalContent from 'components/OfferPage/Villas/VillasPageAmenitiesModalContent'
import ModalContext from 'contexts/ModalContext'
import { EmptyArray } from 'lib/array/arrayUtils'
import { getHotelOfferUrl } from 'lib/offer/offerPageURL'
import { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import SearchTileStackAmenitiesList from '../shared/SearchTileStackAmenitiesList'
import LoyaltyInclusions from 'loyalty/components/LoyaltyInclusions'
import useLoyaltyBenefitInclusions from 'loyalty/hooks/useLoyaltyBenefitInclusions'
import SearchTileRoomBreakdown from 'components/SearchKit/SearchTiles/shared/SearchTileRoomBreakdown'
import type { SearchTileDensity } from 'components/SearchKit/SearchTiles/searchTileDensity'
import { CRO_COMPACT_MAPPED_INCLUSION_CATEGORIES, getAccommodationSearchTileView } from './accommodationSearchTileView'

interface Props {
  offer: App.VillaOffer | App.VillaOfferSummary
  filters?: App.OfferListFilters
  pkg?: App.HotelPackage
  /**
   * @default default
   */
  density?: SearchTileDensity
  hideRoomBreakdown?: boolean
}

const VILLA_AMENITIES_TRUNCATE_AFTER = 6

function VillaSearchTileFeatures({ offer, filters, pkg, density = 'default', hideRoomBreakdown = false }: Props) {
  const tileView = getAccommodationSearchTileView(density)
  const isCroCompact = density === 'cro-compact'
  const intl = useIntl()
  const vipServicesGroupLabel = intl.formatMessage({
    id: 'Mmuv9qKpRK7wqTFcefe1',
    defaultMessage: 'VIP services',
    description: 'Amenity group heading for villa package inclusions shown in the villa amenities modal and search tile amenities list',
  })
  const VIPServices = pkg?.inclusions?.map((inclusion): App.PackageRoomAmenity => {
    return {
      id: inclusion.id,
      description: inclusion.description,
      group: vipServicesGroupLabel,
    }
  }) ?? []
  const amenities = VIPServices.concat(pkg?.roomType.amenities ?? EmptyArray)
  const loyaltyInclusions = useLoyaltyBenefitInclusions(offer)
  const showLoyaltyInline = !isCroCompact && tileView.showLoyaltyInclusions &&
    amenities.length + loyaltyInclusions.length <= VILLA_AMENITIES_TRUNCATE_AFTER

  const showModal = useContext(ModalContext)
  const openAmenitiesModal = useCallback(() => {
    showModal(
      <Modal
        title={offer.property.name}
        primaryActionTo={getHotelOfferUrl(offer.id, offer.type)}
        primaryActionText={intl.formatMessage({
          id: 'dlywzFwMOEUxbPfSbsSp',
          defaultMessage: 'View offer',
          description: 'CTA button in villa amenities modal to view the offer',
        })}
      >
        <VillasPageAmenitiesModalContent
          amenities={amenities}
          loyaltyInclusions={loyaltyInclusions}
        />
      </Modal>,
    )
  }, [showModal, offer, amenities, loyaltyInclusions, intl])

  if (!amenities.length) {
    return hideRoomBreakdown ? null : <SearchTileRoomBreakdown offer={offer} filters={filters} isCroCompact={isCroCompact} />
  }

  return <>
    <SearchTileStackAmenitiesList
      amenities={amenities}
      onMoreClick={isCroCompact ? undefined : openAmenitiesModal}
      truncateAfter={isCroCompact ? CRO_COMPACT_MAPPED_INCLUSION_CATEGORIES : VILLA_AMENITIES_TRUNCATE_AFTER}
      additionalHiddenCount={showLoyaltyInline || isCroCompact ? 0 : loyaltyInclusions.length}
      singleColumn={isCroCompact}
      variant={isCroCompact ? 'small' : undefined}
    />
    {showLoyaltyInline && <LoyaltyInclusions offer={offer} variant="search-tile" />}
    {!hideRoomBreakdown && <SearchTileRoomBreakdown
      offer={offer}
      filters={filters}
      isCroCompact={isCroCompact}
    />}
  </>
}

export default VillaSearchTileFeatures
